import React from 'react';
import { connect, DispatchProp } from 'react-redux';

import { SavingThrowsSummary, HtmlContent } from '@dndbeyond/character-components/es';
import {
    Ability,
    AbilityUtils,
    rulesEngineSelectors,
    Constants,
    DataOrigin,
    DeathSaveInfo,
    DiceAdjustment,
    RuleData,
    RuleDataUtils,
    SituationalSavingThrowInfoLookup,
    CharacterTheme,
} from '@dndbeyond/character-rules-engine/es';
import { IRollContext } from '@dndbeyond/dice';

import SavingThrowsDetails from '../../../../CharacterSheet/components/SavingThrowsDetails';
import { sidebarActions } from '../../../actions';
import { SidebarHeader, SidebarSubheading } from '../../../components/Sidebar';
import { appEnvSelectors, characterRollContextSelectors } from '../../../selectors';
import { SharedAppState } from '../../../stores/typings';
import { PaneComponentEnum, PaneIdentifierUtils, PaneUtils } from '../../../utils';

interface Props extends DispatchProp {
    abilities: Array<Ability>;
    ruleData: RuleData;
    savingThrowDiceAdjustments: Array<DiceAdjustment>;
    situationalBonusSavingThrowsLookup: SituationalSavingThrowInfoLookup;
    deathSaveInfo: DeathSaveInfo;
    diceEnabled: boolean;
    theme: CharacterTheme;
    characterRollContext: IRollContext;
}
class SavingThrowsPane extends React.PureComponent<Props> {
    static defaultProps = {
        diceEnabled: false,
    };

    handleAbilityClick = (ability: Ability): void => {
        const { dispatch } = this.props;

        dispatch(
            sidebarActions.paneHistoryPush(
                PaneComponentEnum.ABILITY_SAVING_THROW,
                PaneIdentifierUtils.generateAbilitySavingThrows(AbilityUtils.getId(ability)),
            ),
        );
    };

    handleDataOriginClick = (dataOrigin: DataOrigin): void => {
        const { dispatch } = this.props;

        let component = PaneUtils.getDataOriginComponentInfo(dataOrigin);
        if (component.type !== PaneComponentEnum.ERROR_404) {
            dispatch(sidebarActions.paneHistoryPush(component.type, component.identifiers));
        }
    };

    renderDescription = (): React.ReactNode => {
        const { ruleData } = this.props;

        let rulesText = RuleDataUtils.getRule(Constants.RuleKeyEnum.SAVING_THROWS, ruleData);
        if (rulesText === null || rulesText.description === null) {
            return null;
        }

        return <HtmlContent className="ct-saving-throws-pane__description" html={rulesText.description} />;
    };

    render() {
        const {
            abilities,
            ruleData,
            savingThrowDiceAdjustments,
            situationalBonusSavingThrowsLookup,
            deathSaveInfo,
            theme,
            characterRollContext,
        } = this.props;

        return (
            <div className="ct-saving-throws-pane">
                <SidebarHeader>Saving Throws</SidebarHeader>
                <SavingThrowsSummary
                    abilities={abilities}
                    situationalBonusSavingThrowsLookup={situationalBonusSavingThrowsLookup}
                    onClick={this.handleAbilityClick}
                    diceEnabled={false}
                    theme={theme}
                    rollContext={characterRollContext}
                />
                <div className="ct-saving-throws-pane__details">
                    <SidebarSubheading>Saving Throw Modifiers</SidebarSubheading>
                    <SavingThrowsDetails
                        theme={theme}
                        abilities={abilities}
                        ruleData={ruleData}
                        savingThrowDiceAdjustments={savingThrowDiceAdjustments}
                        deathSaveInfo={deathSaveInfo}
                        onDataOriginClick={this.handleDataOriginClick}
                    />
                </div>
                {this.renderDescription()}
            </div>
        );
    }
}

function mapStateToProps(state: SharedAppState) {
    return {
        abilities: rulesEngineSelectors.getAbilities(state),
        ruleData: rulesEngineSelectors.getRuleData(state),
        situationalBonusSavingThrowsLookup: rulesEngineSelectors.getSituationalBonusSavingThrowsLookup(state),
        savingThrowDiceAdjustments: rulesEngineSelectors.getSavingThrowDiceAdjustments(state),
        deathSaveInfo: rulesEngineSelectors.getDeathSaveInfo(state),
        diceEnabled: appEnvSelectors.getDiceEnabled(state),
        theme: rulesEngineSelectors.getCharacterTheme(state),
        characterRollContext: characterRollContextSelectors.getCharacterRollContext(state),
    };
}

export default connect(mapStateToProps)(SavingThrowsPane);
